import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useRequestCode, useSignIn } from '@/api';
import { SignInBody } from '@/api/types';
import { RequestCodeIcon } from '@/components/icons/modal';
import { Loader } from '@/components/Loader/Loader.tsx';
import { Button } from '@/components/shared/Button';
import { Checkbox } from '@/components/shared/Checkbox/Checkbox.tsx';
import { Input } from '@/components/shared/Input/Input.tsx';
import { SignInSchema } from '@/components/shared/Modal/content/sign-in/constants.ts';
import { MainConfig } from '@/config/MainConfig.ts';
import { numberMask, numberPlaceholder } from '@/constants';
import { useTimer } from '@/hooks';
import { useAuthStore } from '@/store';
import { formatNumber, getRemainingSeconds, isMobileApp } from '@/utils';

import cn from './styles.module.scss';

interface IModalSignInContentProps {
    openSignUp: (signingUp: boolean) => void;
    handleClose: VoidFunction;
}

export type SignInFormData = Omit<SignInBody, 'msisdn'> & { msisdn: string };

export const ModalSignInContent = ({ openSignUp, handleClose }: IModalSignInContentProps) => {
    const { msisdn, setMsisdn } = useAuthStore();

    const [isTimerActive, setIsTimerActive] = useState(false);
    const [isCodeRequested, setIsCodeRequested] = useState(false);
    const [isCodeIncorrect, setIsCodeIncorrect] = useState(false);

    const { mutateAsync: signIn, isPending: isSignInPending } = useSignIn();
    const { mutateAsync: requestCode, isPending: isRequestCodePending } = useRequestCode();

    const {
        control,
        trigger,
        formState,
        getValues,
        getFieldState,
        handleSubmit,
        setValue,
        clearErrors,
        setError,
    } = useForm<SignInFormData>({
        defaultValues: {
            msisdn,
            code: '',
            agreement: false,
        },
        resolver: zodResolver(SignInSchema),
    });

    const isNumberFieldInvalid = getFieldState('msisdn').invalid;

    const remainingSeconds = useMemo(
        () =>
            getRemainingSeconds(
                localStorage.getItem('timestamp') || Math.ceil(Date.now() + 1000 * 60)
            ),
        [isRequestCodePending]
    );

    const { time, start, reset } = useTimer(remainingSeconds);

    const handleOpenSignUp = () => {
        openSignUp(true);
    };

    const handleRequestCode = async () => {
        clearErrors('code');
        const errors = await requestCode(formatNumber(getValues('msisdn')));
        const userNotFound = !!errors?.find(error => error.type === 'user_not_found');
        const cooldown = errors?.find(error => error.type === 'cooldown');

        if (errors) {
            if (userNotFound) {
                setMsisdn(getValues('msisdn'));
                handleOpenSignUp();
            }

            if (cooldown) {
                localStorage.setItem('timestamp', String(cooldown.data.resendCooldown));
                start();
                setIsTimerActive(true);
                setIsCodeRequested(true);
            }
            return;
        }
        start();
        setIsTimerActive(true);
        setIsCodeRequested(true);
    };

    const onSubmit = async (data: SignInFormData) => {
        const formattedNumber = formatNumber(data.msisdn);
        const formattedData: SignInBody = { ...data, msisdn: formattedNumber };
        const authErrors = await signIn(formattedData);
        const isIncorrectPassword = !!authErrors?.find(
            error => error.type === 'incorrect_password_code'
        );
        const isNotValidNumber = !!authErrors?.find(
            error => error.property === 'msisdn' && error.type === 'not_valid'
        );
        const userNotFound = !!authErrors?.find(error => error.type === 'user_not_found');
        const codeExpired = authErrors?.find(
            error => error.type === 'expired' && error.property === 'password'
        );
        if (authErrors) {
            if (isIncorrectPassword) {
                setIsCodeIncorrect(true);
                setError('code', {
                    type: 'incorrect_password_code',
                    message: 'Неверный код. Необходимо запросить новый',
                });
            }

            if (codeExpired) {
                setIsCodeIncorrect(true);
                setIsTimerActive(false);
                setIsCodeRequested(false);
                setError('code', {
                    type: 'incorrect_password_code',
                    message: 'Истек срок действия кода',
                });
            }

            if (isNotValidNumber) {
                setError('msisdn', { type: 'invalid', message: 'Неверный формат номера' });
            }

            if (userNotFound) {
                setMsisdn(getValues('msisdn'));
                handleOpenSignUp();
            }
            return;
        }
        handleClose();
    };

    const BeforeCodeReqBtns = isSignInPending ? (
        <Loader mini />
    ) : (
        <>
            {!isCodeIncorrect && (
                <Button disabled={!formState.isValid} type="submit">
                    Войти
                </Button>
            )}
            <Button
                disabled={isNumberFieldInvalid}
                onClick={handleRequestCode}
                background={isCodeIncorrect ? 'primary' : 'transparent'}
                className={cn.requestCodeBtn}
            >
                Получить новый код
            </Button>
        </>
    );

    const AfterCodeReqBtns = isSignInPending ? (
        <Loader mini />
    ) : (
        <>
            <Button
                disabled={isTimerActive || isNumberFieldInvalid}
                onClick={handleRequestCode}
                background="transparent"
                className={clsx(cn.requestCodeBtnAgain, isTimerActive && cn.grey)}
            >
                {!isTimerActive && <span>{RequestCodeIcon} </span>}
                Запросить код повторно
                {isTimerActive && ` через ${time} сек`}
            </Button>
            <Button disabled={!formState.isValid} type="submit">
                Войти
            </Button>
        </>
    );

    useEffect(() => {
        if (time <= 0) {
            setIsTimerActive(false);
            localStorage.removeItem('timestamp');
            reset();
        }
    }, [reset, time]);

    useEffect(() => {
        trigger();
        setMsisdn('');
    }, []);

    return (
        <div className={cn.signInContent}>
            <div className={cn.modalHeader}>
                <h2>Вход</h2>
            </div>
            <div className={cn.modalForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={cn.modalFormInputContainer}>
                        <div className={cn.inputWrapper}>
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur, name },
                                    fieldState: { error },
                                }) => (
                                    <Input
                                        mask={numberMask}
                                        label="Номер телефона*"
                                        value={value}
                                        name={name}
                                        onBlur={onBlur}
                                        onChange={e => {
                                            onChange(e);
                                            trigger();
                                        }}
                                        type="tel"
                                        error={error?.type === 'invalid'}
                                        helperText={error?.type === 'invalid' ? error.message : ''}
                                        placeholder={numberPlaceholder}
                                        onClear={e => {
                                            e.preventDefault();
                                            clearErrors('msisdn');
                                            setValue('msisdn', '');
                                            trigger();
                                        }}
                                    />
                                )}
                                name="msisdn"
                                control={control}
                            />
                        </div>
                        <div className={cn.inputWrapper}>
                            <Controller
                                render={({ field, fieldState: { error } }) => (
                                    <Input
                                        label="Код подтверждения (действует 30 дней)*"
                                        type="tel"
                                        error={error?.type === 'incorrect_password_code'}
                                        helperText={
                                            error?.type === 'incorrect_password_code'
                                                ? error?.message
                                                : ''
                                        }
                                        placeholder="Ввести код из СМС"
                                        mask="999999"
                                        maskChar=""
                                        onClear={e => {
                                            e.preventDefault();
                                            clearErrors('code');
                                            setValue('code', '');
                                            trigger();
                                        }}
                                        {...field}
                                    />
                                )}
                                name="code"
                                control={control}
                            />
                        </div>
                        <div className={cn.modalFormCheckBox}>
                            <Controller
                                render={({ field }) => <Checkbox {...field} />}
                                name="agreement"
                                control={control}
                            />
                        </div>
                    </div>

                    <div className={cn.modalFormBtnContainer}>
                        {isTimerActive || isCodeRequested ? AfterCodeReqBtns : BeforeCodeReqBtns}
                        <Button
                            className={cn.signUpBtn}
                            onClick={handleOpenSignUp}
                            background="transparent"
                        >
                            Нет аккаунта? Зарегистрироваться
                        </Button>
                    </div>
                </form>
            </div>
            {!isMobileApp() && (
                <div className={cn.modalFooter}>
                    <h3>Удобнее в приложении</h3>
                    <div className={cn.modalFooterIconContainer}>
                        <a
                            target="_blank"
                            className={cn.imgWrapper}
                            href={MainConfig.appLink.googlePlay}
                            rel="noreferrer"
                        >
                            <img src="/images/icons/mobile-app/google-play.svg" alt="" />
                        </a>
                        <a
                            target="_blank"
                            className={cn.imgWrapper}
                            href={MainConfig.appLink.appStore}
                            rel="noreferrer"
                        >
                            <img src="/images/icons/mobile-app/app-store.svg" alt="" />
                        </a>
                        <a
                            target="_blank"
                            className={cn.imgWrapper}
                            href={MainConfig.appLink.apk}
                            rel="noreferrer"
                        >
                            <img src="/images/icons/mobile-app/apk.svg" alt="" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};
